import React from "react";
import { debounceFn } from "@/utils";
import { event, EVENT_MAP } from "@/utils/event";
import { useStore } from "@/store";

export const useDebounceFn = (
  callback: (...args: any[]) => any,
  delay: number,
  deps: any[] = [],
) => {
  return React.useCallback(debounceFn(callback, delay), deps);
};

/**
 * @description 自动滚动定位当前选中(active)的节点
 */
export const useActiveScroll = ({
  activeIndex,
  wrapRef,
  waisMS,
  activeClassName,
}: {
  wrapRef: React.RefObject<HTMLUListElement | HTMLDivElement>;
  activeIndex: number;
  activeClassName: string;
  waisMS?: number;
}) => {
  React.useLayoutEffect(() => {
    const timer = setTimeout(
      () => {
        if (activeIndex === -1) return;
        if (!wrapRef.current) return console.warn("未找到对应的容器节点!");

        const $wrapDom = wrapRef.current;
        const $selectedDom = document.querySelector(activeClassName) as HTMLLIElement;

        if (!$selectedDom) {
          return console.warn("未找到对应的active dom节点!");
        }
        const scrollX = Math.max($selectedDom.offsetLeft - $selectedDom.offsetWidth, 0);
        if (!$wrapDom.scrollTo) {
          $wrapDom.scrollLeft = scrollX;
        } else {
          $wrapDom.scrollTo({ behavior: "smooth", left: scrollX });
        }
      },
      waisMS === undefined ? 50 : waisMS,
    );
    return () => clearTimeout(timer);
  }, [activeIndex]);
};

/**
 * @description 监听媒体查询断点，返回当前是否匹配
 * @example
 * const isMobile = useMediaQuery("(max-width: 767px)");
 */
export const useMediaQuery = (query: string) => {
  const [matches, setMatches] = React.useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false,
  );

  React.useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);

  return matches;
};

/**
 * @description 监听全局下拉刷新事件
 * @example
 * usePullToRefresh(() => {
 *   refetch(); // 重新请求数据
 * });
 */
export const usePullToRefresh = (cb: () => void, deps: any[] = []) => {
  React.useEffect(() => {
    const off = event.on(EVENT_MAP.LAYOUT__PULL_TO_REFRESH, cb);
    return off;
  }, deps);
};

/**
 * @description 组件挂载时禁用下拉刷新，卸载时自动恢复
 * @example
 * // 在不需要下拉刷新的页面中调用
 * useDisablePullToRefresh();
 */
export const useDisablePullToRefresh = () => {
  const { setStoreState } = useStore();
  React.useEffect(() => {
    setStoreState({ pullToRefreshEnabled: false });
    return () => setStoreState({ pullToRefreshEnabled: true });
  }, []);
};
